/**
 * Factures, sections 16 à 33 du document 11.
 *
 * Le registre legacy porte six statuts, la documentation V3 en recommande neuf.
 * La correspondance est celle gelée dans `docs/migration/status-mappings.md`.
 */

export const invoiceStatuses = [
  'DRAFT',
  'READY',
  'ISSUED',
  'SENT',
  'PARTIALLY_PAID',
  'PAID',
  'OVERDUE',
  'CANCELLED',
  'ARCHIVED',
] as const

export type InvoiceStatus = (typeof invoiceStatuses)[number]

/**
 * Correspondance gelée du registre.
 *
 * `envoyee` y est rangé sur `SENT` **ou** `ISSUED` avec une confiance faible.
 * Les lignes récentes portent `issued_at` et `sent_at`, qui tranchent; les
 * lignes héritées n'ont ni l'une ni l'autre et restent lues comme `SENT`.
 * Aucune date n'est reconstituée.
 */
export const legacyInvoiceStatusMap = {
  brouillon: 'DRAFT',
  envoyee: 'SENT',
  partiellement_payee: 'PARTIALLY_PAID',
  payee: 'PAID',
  en_retard: 'OVERDUE',
  annulee: 'CANCELLED',
} as const satisfies Record<string, InvoiceStatus>

export const invoiceStatusLabel: Record<InvoiceStatus, string> = {
  DRAFT: 'Brouillon',
  READY: 'Prête',
  ISSUED: 'Émise',
  SENT: 'Envoyée',
  PARTIALLY_PAID: 'Partiellement payée',
  PAID: 'Payée',
  OVERDUE: 'En retard',
  CANCELLED: 'Annulée',
  ARCHIVED: 'Archivée',
}

export const invoiceStatusTone: Record<InvoiceStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> =
  {
    DRAFT: 'neutral',
    READY: 'info',
    ISSUED: 'info',
    SENT: 'info',
    PARTIALLY_PAID: 'warning',
    PAID: 'success',
    OVERDUE: 'danger',
    CANCELLED: 'danger',
    ARCHIVED: 'neutral',
  }

export interface InvoiceRecord {
  id: string
  number: string
  status: InvoiceStatus
  clientId: string
  clientDisplayName: string
  clientNumber?: string
  contractId?: string
  contractNumber?: string
  /**
   * Refonte Facturation, Phase 5 (audit UX/UI §5.6) : statut réel actuel du
   * Contrat — jamais une transition prédite (aucune automatisation
   * paiement→activation n'existe côté serveur, cf. `tasks.md`/`plans.md`).
   * Bruts (`statut`/`status_v2` legacy), même patron que
   * `ClientContractSummary` — la dérivation d'affichage reste côté
   * présentation (`deriveContractDisplayStatus`).
   */
  contractLegacyStatus?: string
  contractStatusV2?: string
  /** Échéance de la Facture; c'est elle qui rend une Facture en retard. */
  dueOn?: string
  issuedAt?: string
  sentAt?: string
  cancelledAt?: string
  cancellationReason?: string
  subtotalCents: number
  taxTotalCents: number
  totalCents: number
  balanceCents: number
  /**
   * Paiements valides (`RECORDED`) imputés à la Facture, en cents. Absent quand la lecture n'a pas pu
   * se faire (droits, erreur) : l'écran montre alors « — », jamais total − solde.
   */
  paidCents?: number
  /**
   * Crédits imputés : notes de crédit appliquées et crédit au compte, les deux déductions de
   * `recalculate_invoice_v1` (total − crédits = exigible; exigible − paiements = solde).
   */
  creditedCents?: number
  createdAt: string
  updatedAt: string
}

export interface InvoiceStats {
  total: number
  draft: number
  issued: number
  partiallyPaid: number
  paid: number
  overdue: number
  cancelled: number
  /** Somme des soldes restant dus, hors Factures annulées. */
  outstandingCents: number
}

export interface InvoiceListFilters {
  page: number
  pageSize: number
  status?: InvoiceStatus
  search?: string
  /** Sprint H : restreint aux Factures d'un Client — fiche Client, section E. */
  clientId?: string
  /**
   * Refonte Facturation, Phase 5 (audit UX/UI §5.6) : restreint aux Factures
   * d'un Contrat — trouver la prochaine échéance impayée après un paiement.
   */
  contractId?: string
  /**
   * Sprint H, onglet « À encaisser » : Factures émises (ni brouillon ni
   * annulée) dont le solde est supérieur à zéro. Filtré côté serveur — pas de
   * chargement complet suivi d'un tri en mémoire (section 20 de la tâche).
   */
  openOnly?: boolean
  /**
   * Système de listes, vue « En retard » : factures ouvertes (même définition que `openOnly`) dont
   * l'échéance est passée — la définition de `overdue_cents` des KPI Facturation et Vue d'ensemble.
   * Le statut stocké `en_retard` ne suffit pas : le retard se constate à l'échéance.
   */
  overdueOnly?: boolean
  /**
   * Vue Recouvrement : restreint à l'état réel actuel du Contrat lié (`status_v2`). Aucune transition
   * n'est déduite ici; la suspension relève de la règle existante côté serveur.
   */
  contractStatus?: 'ACTIVE' | 'SUSPENDED'
  /**
   * `dueAsc` trie par échéance croissante : en retard d'abord, puis échéance
   * du jour, puis le reste — l'ordre de priorité de l'onglet « À encaisser ».
   */
  sort?: 'dueAsc' | 'createdDesc'
}

export interface InvoicePage {
  items: InvoiceRecord[]
  page: number
  pageSize: number
  total: number
  stats: InvoiceStats
  meta: { source: 'supabase' | 'fixtures' | 'fixture-fallback'; retrievedAt: string; fallbackReason?: string }
}

export interface InvoiceDetailResult {
  invoice: InvoiceRecord
  meta: InvoicePage['meta']
}

/**
 * Statut effectif, section 32.
 *
 * L'ordre des règles est celui de la documentation : l'annulation domine tout,
 * puis le solde, puis l'échéance. Le statut stocké ne sert qu'ensuite. Un
 * `en_retard` figé en base sur une Facture soldée serait ainsi corrigé à
 * l'affichage sans qu'on réécrive la donnée.
 */
export function effectiveInvoiceStatus(
  invoice: Pick<InvoiceRecord, 'status' | 'balanceCents' | 'totalCents' | 'dueOn'>,
  now: Date = new Date(),
): InvoiceStatus {
  if (invoice.status === 'CANCELLED' || invoice.status === 'ARCHIVED') return invoice.status
  if (invoice.status === 'DRAFT' || invoice.status === 'READY') return invoice.status
  if (invoice.balanceCents <= 0) return 'PAID'
  if (invoice.dueOn && new Date(invoice.dueOn).getTime() < now.getTime()) return 'OVERDUE'
  if (invoice.balanceCents < invoice.totalCents) return 'PARTIALLY_PAID'
  return invoice.status
}

/**
 * Émission constatée, source unique de l'écran Facture.
 *
 * `issued_at` n'existe que sur les lignes produites par V3 : les Factures
 * héritées en sont dépourvues, y compris quand elles ont bel et bien été
 * émises puis payées. Lire la date pour trancher l'émission faisait donc
 * cohabiter « Payée » et « Non émise » sur la même page. Le statut tranche,
 * la date n'est plus qu'une précision — et aucune date n'est reconstituée.
 */
export function isInvoiceIssued(invoice: Pick<InvoiceRecord, 'status'>): boolean {
  return invoice.status !== 'DRAFT' && invoice.status !== 'READY'
}

/** Date du jour à Toronto (`AAAA-MM-JJ`), fuseau configuré des échéances. */
export function torontoToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

export function formatMoney(cents: number): string {
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100)
}

/** Méthodes de paiement, section 56; registre fermé côté serveur. */
export const paymentMethods = [
  { key: 'CASH', label: 'Comptant' },
  { key: 'CHEQUE', label: 'Chèque' },
  { key: 'E_TRANSFER', label: 'Virement Interac' },
  { key: 'CARD', label: 'Carte' },
  { key: 'ONLINE', label: 'Paiement en ligne' },
  { key: 'OTHER', label: 'Autre' },
] as const

export type PaymentMethod = (typeof paymentMethods)[number]['key']

export interface PaymentRecord {
  id: string
  amountCents: number
  method?: PaymentMethod
  reference?: string
  receivedOn: string
  status: 'RECORDED' | 'CANCELLED'
  cancellationReason?: string
}

/**
 * Paiement vu depuis le suivi financier, sections 14 et 15.
 *
 * L'écran des Paiements regarde les mouvements de toute l'entreprise, pas ceux
 * d'une seule Facture : il porte donc le client et le numéro de facture, sans
 * quoi une ligne serait illisible.
 */
export interface PaymentListItem extends PaymentRecord {
  invoiceId: string
  /** Sert aussi de référence de route : `/invoices/:number`. */
  invoiceNumber: string
  clientName: string
}

export interface PaymentListFilters {
  /** Nombre de jours regardés en arrière; `null` pour tout l'historique. */
  windowDays: number | null
  status: 'ALL' | 'RECORDED' | 'CANCELLED'
  search: string
  /** Pagination serveur, à partir de 1. */
  page: number
  pageSize: number
}

export interface PaymentPage {
  items: PaymentListItem[]
  page: number
  pageSize: number
  /** Nombre de paiements du périmètre filtré (période, recherche, statut), toutes pages confondues. */
  total: number
  /** Décomptes par statut sur la même période et la même recherche, toutes pages confondues. */
  recorded: number
  cancelled: number
  meta: { source: 'supabase' | 'fixtures'; retrievedAt: string }
}

/**
 * Note de crédit, sections 9, 10 et 77.
 *
 * Un Crédit dit ce qu'on renonce à réclamer, avec sa raison. Il ne retouche pas
 * le montant facturé et n'est jamais un paiement négatif.
 */
export const creditStatuses = ['DRAFT', 'ISSUED', 'APPLIED', 'CANCELLED'] as const
export type CreditStatus = (typeof creditStatuses)[number]

export const creditStatusLabel: Record<CreditStatus, string> = {
  DRAFT: 'Préparé',
  ISSUED: 'Émis',
  APPLIED: 'Imputé',
  CANCELLED: 'Annulé',
}

export const creditStatusTone: Record<CreditStatus, 'neutral' | 'info' | 'success' | 'danger'> = {
  DRAFT: 'neutral',
  ISSUED: 'info',
  APPLIED: 'success',
  CANCELLED: 'danger',
}

export interface CreditNote {
  id: string
  number: string
  invoiceId: string
  amountCents: number
  reason: string
  status: CreditStatus
  issuedAt?: string
  appliedAt?: string
  cancellationReason?: string
}

export interface CreateCreditNoteCommand {
  idempotencyKey: string
  invoiceId: string
  amountCents: number
  reason: string
}

export interface TransitionCreditNoteCommand {
  idempotencyKey: string
  creditNoteId: string
  action: 'ISSUE' | 'APPLY' | 'CANCEL'
  reason?: string
}

/**
 * KPI de la vue d'ensemble Facturation, Sprint H sections 4-5.
 *
 * Calculés côté serveur (agrégats SQL, jamais une somme d'une liste chargée
 * en React). Un champ à `null` signifie « non autorisé pour ce rôle » — la
 * fonction serveur ne renvoie pas la valeur plutôt que zéro, pour ne jamais
 * laisser croire qu'un solde Paiements est nul à un rôle Paiements-interdit
 * (section 26, cas asymétrique).
 */
export interface BillingKpis {
  /** Somme des soldes ouverts (Factures émises, non annulées). `null` sans `invoice.read`. */
  toCollectCents: number | null
  /** Part de `toCollectCents` dont l'échéance est dépassée. `null` sans `invoice.read`. */
  overdueCents: number | null
  /** Paiements `RECORDED` du mois calendaire en cours. `null` sans `payment.read`. */
  collectedThisMonthCents: number | null
  /** Factures ni brouillon ni annulée ni entièrement payée. `null` sans `invoice.read`. */
  openInvoicesCount: number | null
  /** Somme des prix des Contrats VIP actifs (jamais facturés, donc hors `toCollectCents`). `null` sans `contract.read`. */
  vipTotalCents: number | null
  /** Nombre de Contrats VIP actifs. `null` sans `contract.read`. */
  vipCount: number | null
  meta: { source: 'supabase' | 'fixtures'; retrievedAt: string }
}

/** Facture encaissable, pour choisir la cible d'un paiement depuis l'écran dédié. */
export interface OpenInvoiceOption {
  id: string
  number: string
  clientName: string
  balanceCents: number
}

/** Émission du Reçu d'un Paiement, section 86. */
export interface SendReceiptCommand {
  idempotencyKey: string
  paymentId: string
}

/** Envoi d'une Note de crédit au Client, sections 10 et 77. */
export interface SendCreditNoteCommand {
  idempotencyKey: string
  creditNoteId: string
}

export interface RecordPaymentCommand {
  idempotencyKey: string
  invoiceId: string
  amountCents: number
  method: PaymentMethod
  receivedAt: string
  reference?: string
  notes?: string
  /**
   * Autorise un encaissement supérieur au solde, section 63. L'excédent
   * rejoint le compte du Client; sans cet aveu explicite, le serveur refuse,
   * un trop-perçu étant plus souvent une faute de frappe qu'une avance.
   */
  allowOverpayment?: boolean
}

export interface RecordPaymentResult {
  paymentId: string
  invoiceId: string
  number: string
  amountCents: number
  balanceCents: number
  invoiceStatus: string
  /** Part de l'encaissement versée au compte du Client, en cents. */
  creditedCents?: number
  /** Solde du compte de crédit après l'opération, en cents. */
  accountBalanceCents?: number
  /** Vrai lorsqu'une reprise idempotente a rendu le paiement déjà enregistré. */
  replayed: boolean
}

/** Mouvement du compte de crédit d'un Client, section 9. */
export interface ClientCreditEntry {
  id: string
  amountCents: number
  kind: 'OVERPAYMENT' | 'APPLICATION' | 'REVERSAL'
  reason: string
  invoiceId?: string
  createdAt: string
}

export interface ClientCreditAccount {
  clientId: string
  balanceCents: number
  entries: ClientCreditEntry[]
}

export interface ApplyClientCreditCommand {
  idempotencyKey: string
  invoiceId: string
  amountCents: number
}

export interface ApplyClientCreditResult {
  entryId: string
  invoiceId: string
  number: string
  amountCents: number
  balanceCents: number
  accountBalanceCents: number
  replayed: boolean
}

/** Ligne de Facture, section 18. Les montants sont en cents, section 20. */
export interface InvoiceItem {
  id: string
  description: string
  quantityMilli: number
  unitPriceCents: number
  subtotalCents: number
  taxable: boolean
  order: number
}

/** Ligne saisie, avant que la base n'en établisse le sous-total. */
export interface InvoiceItemDraft {
  description: string
  quantityMilli: number
  unitPriceCents: number
  taxable: boolean
}

/**
 * Remplacement des lignes d'un brouillon, section 13.
 *
 * `expectedUpdatedAt` est le jeton de concurrence : deux personnes sur le même
 * brouillon ne s'écrasent pas, la seconde est refusée et rafraîchit.
 */
export interface ReplaceInvoiceItemsCommand {
  idempotencyKey: string
  invoiceId: string
  expectedUpdatedAt: string
  items: InvoiceItemDraft[]
}

export interface ReplaceInvoiceItemsResult {
  invoiceId: string
  items: number
  subtotal: number
  total: number
  updatedAt: string
}

/** Une quantité de 1 n'apporte rien : la section 19 dit de ne l'afficher qu'utile. */
export function formatQuantity(quantityMilli: number): string | undefined {
  if (quantityMilli === 1000) return undefined
  return new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 3 }).format(quantityMilli / 1000)
}
